import type { TransactionRecord, TransactionItem } from '@/types/Transaction'

interface CSVRecord {
  date: string
  transaction_number: string
  transaction_type: string
  party_name: string
  total_amount: string
  payment_status: string
  payment_method: string
  transaction_status: string
  items_count: string
  total_weight: string
  notes: string
}

function formatDate(date: Date | { toDate: () => Date } | string | null | undefined): string {
  if (!date) return ''
  // Handle Firestore Timestamp
  if (date && typeof date === 'object' && 'toDate' in date) {
    return date.toDate().toISOString().split('T')[0]
  }
  // Handle Date object
  if (date instanceof Date) {
    return date.toISOString().split('T')[0]
  }
  // Handle string
  if (typeof date === 'string') {
    return new Date(date).toISOString().split('T')[0]
  }
  return ''
}

function formatCurrency(amount: number): string {
  return amount.toFixed(2)
}

function calculateTotalWeight(items: TransactionItem[]): number {
  return items.reduce((total, item) => {
    const netWeight = item.weight?.net || 0
    return total + netWeight * item.quantity
  }, 0)
}

function escapeCSV(value: string): string {
  // If value contains comma, quote, or newline, wrap in quotes and escape quotes
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function convertToCSV(records: CSVRecord[]): string {
  const headers = [
    'Date',
    'Transaction Number',
    'Transaction Type',
    'Party Name',
    'Total Amount',
    'Payment Status',
    'Payment Method',
    'Transaction Status',
    'Items Count',
    'Total Weight (lbs)',
    'Notes',
  ]

  const rows = records.map((record) => {
    return [
      escapeCSV(record.date),
      escapeCSV(record.transaction_number),
      escapeCSV(record.transaction_type),
      escapeCSV(record.party_name),
      escapeCSV(record.total_amount),
      escapeCSV(record.payment_status),
      escapeCSV(record.payment_method),
      escapeCSV(record.transaction_status),
      escapeCSV(record.items_count),
      escapeCSV(record.total_weight),
      escapeCSV(record.notes),
    ].join(',')
  })

  return [headers.join(','), ...rows].join('\n')
}

export function exportTransactionsToCSV(
  transactions: TransactionRecord[],
  filename?: string,
): void {
  if (transactions.length === 0) {
    return
  }

  // Transform transactions to CSV format
  const records = transactions.map((tx) => {
    const baseAmount = tx.total_amount || 0
    // Show negative amounts for purchases, positive for sales
    const displayAmount = tx.transaction_type === 'purchase' ? -baseAmount : baseAmount
    const totalWeight = calculateTotalWeight(tx.items || [])

    return {
      date: formatDate(tx.transaction_date),
      transaction_number: tx.transaction_number || '',
      transaction_type: tx.transaction_type || '',
      party_name: tx.partyName || tx.party_data?.name || '',
      total_amount: formatCurrency(displayAmount),
      payment_status: tx.payment_status || '',
      payment_method: tx.payment_method || '',
      transaction_status: tx.transaction_status || '',
      items_count: (tx.items?.length || 0).toString(),
      total_weight: formatCurrency(totalWeight),
      notes: (tx.notes || '').replace(/\n/g, ' ').replace(/,/g, ';'), // Clean up notes for CSV
    }
  })

  // Calculate summary statistics
  const totalAmount = records.reduce((sum, r) => sum + parseFloat(r.total_amount), 0)
  const totalWeight = records.reduce((sum, r) => sum + parseFloat(r.total_weight), 0)
  const purchaseCount = records.filter((r) => r.transaction_type === 'purchase').length
  const saleCount = records.filter((r) => r.transaction_type === 'sale').length

  // Add summary row
  const summaryRow = {
    date: 'SUMMARY',
    transaction_number: '',
    transaction_type: '',
    party_name: '',
    total_amount: formatCurrency(totalAmount),
    payment_status: '',
    payment_method: '',
    transaction_status: '',
    items_count: records.length.toString(),
    total_weight: formatCurrency(totalWeight),
    notes: `Total: ${records.length} transactions (${purchaseCount} purchases, ${saleCount} sales)`,
  }

  // Convert to CSV
  const csvContent = convertToCSV([...records, summaryRow])

  // Generate filename with timestamp if not provided
  const timestamp = new Date().toISOString().split('T')[0]
  const csvFilename = filename || `transactions-${timestamp}.csv`

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', csvFilename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
